import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import {
  authControllerMe,
  authControllerSwitchContext,
  authControllerExitDelegation,
  authControllerReadPreferences,
} from './auth.api';
import { fetchProfile } from '../../api/mutator';
import { applyServerPreferencesRuntime } from './preferences-runtime';
import { authAtom } from './auth.atoms';
import {
  getAccessToken,
  refreshAccessToken,
  setAccessToken,
  ApiError,
  SESSION_EXPIRED_EVENT,
} from '../../api/http-client';
import { contextQueryKeyPrefix } from '../context/context-query-keys';
import { parseJwtPayload } from './auth.utils';
import type { User, AvailableContext, CurrentContext } from './auth.types';

/** Refresh the access token this far before its expiry to avoid 401 round-trips. */
const PROACTIVE_REFRESH_LEAD_MS = 60_000;
/** Fallback refresh interval when the JWT payload cannot be decoded. */
const FALLBACK_REFRESH_INTERVAL_MS = 14 * 60_000;

export const AUTH_CROSS_TAB_EVENT_KEY = 'finance:auth-event';
export const AUTHORIZATION_CONTEXT_CHANGED_EVENT = 'auth:authorization-context-changed';

/** Validate the server-authoritative profile without client-side scope fallbacks. */
function validateProfile(raw: unknown): User {
  const partial = (raw ?? {}) as Partial<User>;
  if (!partial.id || !partial.email || !partial.status) throw new Error('AUTH_PROFILE_INVALID');
  if (!partial.mfa || typeof partial.mfa.enabled !== 'boolean') {
    throw new Error('AUTH_PROFILE_INVALID');
  }
  if (!Array.isArray(partial.memberships)) throw new Error('AUTH_PROFILE_INVALID');

  const isPlatformScope = partial.scopeKind === 'SYSTEM' || partial.scopeKind === 'PLATFORM';
  if (partial.memberships.length > 0 && !isPlatformScope) {
    if (!partial.activeMembershipId || !partial.activeOrganizationId || !partial.activeBranchId) {
      throw new Error('AUTH_PROFILE_MISSING_ACTIVE_CONTEXT');
    }
  }

  return {
    id: partial.id,
    email: partial.email,
    firstName: partial.firstName ?? '',
    lastName: partial.lastName ?? '',
    phone: partial.phone ?? null,
    status: partial.status,
    hasPassword: partial.hasPassword ?? true,
    credentialManagementMode: partial.credentialManagementMode,
    mfa: partial.mfa,
    memberships: partial.memberships,
    activeMembershipId: partial.activeMembershipId,
    activeOrganizationId: partial.activeOrganizationId,
    activeBranchId: partial.activeBranchId,
    activeClientAccountId: partial.activeClientAccountId,
    activePortfolioAccountId: partial.activePortfolioAccountId,
    persona: partial.persona,
    scopeKind: partial.scopeKind,
    aal: partial.aal,
    entitlementsVersion: partial.entitlementsVersion,
    delegated: partial.delegated === true,
    delegationId: partial.delegationId,
    activeOrganizationName: partial.activeOrganizationName,
    activeBranchName: partial.activeBranchName,
    availableClientAccounts: partial.availableClientAccounts ?? [],
    availablePortfolioAccounts: partial.availablePortfolioAccounts ?? [],
  };
}

export type SessionRestorationState =
  | 'idle'
  | 'restoring'
  | 'authenticated'
  | 'anonymous'
  | 'degraded'
  | 'compromised';

export interface RestorationReason {
  code: string;
  message?: string;
}

export type ContextSwitchState = 'idle' | 'pending' | 'success' | 'error';

export interface SessionContextValue {
  retry: () => void;
  retryCount: number;
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  restorationState: SessionRestorationState;
  restorationReason: RestorationReason | null;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<User | null>;
  currentContext: CurrentContext | null;
  availableContexts: AvailableContext[];
  /** Organization Runtime context switching changes Branch only. */
  switchContext: (membershipId: string, branchId: string) => Promise<void>;
  exitDelegation?: () => Promise<void>;
  contextSwitchState: ContextSwitchState;
  contextSwitchError: string | null;
  invalidateRestoration?: () => void;
}

export const SessionContext = createContext<SessionContextValue>({
  retry: () => {},
  retryCount: 0,
  user: null,
  isAuthenticated: false,
  loading: true,
  restorationState: 'idle',
  restorationReason: null,
  logout: async () => {},
  refreshProfile: async () => null,
  currentContext: null,
  availableContexts: [],
  switchContext: async () => {},
  exitDelegation: async () => {},
  contextSwitchState: 'idle',
  contextSwitchError: null,
  invalidateRestoration: () => {},
});

export const authQueryKeys = { me: ['auth', 'me'] as const };

function buildProfileQueryKey(context: CurrentContext | null, user: User | null): unknown[] {
  return [
    ...authQueryKeys.me,
    context?.membershipId ?? '_none_',
    context?.branchId ?? '_none_',
    user?.entitlementsVersion ?? 0,
    user?.aal ?? '_',
    user?.persona ?? '_',
    user?.scopeKind ?? '_',
  ];
}

function deriveAvailableContexts(user: User | null): AvailableContext[] {
  if (!user) return [];
  const contexts: AvailableContext[] = [];
  for (const membership of user.memberships ?? []) {
    const branchMap = new Map<string, { name: string }>();
    for (const role of membership.roles ?? []) {
      if (role.branchId) branchMap.set(role.branchId, { name: role.branchName ?? '' });
    }
    if (branchMap.size === 0 && user.activeBranchId && membership.id === user.activeMembershipId) {
      branchMap.set(user.activeBranchId, { name: user.activeBranchName ?? '' });
    }
    for (const [branchId, meta] of branchMap.entries()) {
      contexts.push({
        membershipId: membership.id,
        organizationId: membership.organizationId,
        organizationName: membership.organizationName,
        branchId,
        branchName: meta.name,
      });
    }
  }
  return contexts;
}

function deriveCurrentContext(user: User | null): CurrentContext | null {
  if (!user || !user.activeMembershipId || !user.activeOrganizationId) return null;
  const isPlatformScope = user.scopeKind === 'SYSTEM' || user.scopeKind === 'PLATFORM';
  if (isPlatformScope) return null;
  if (!user.activeBranchId) return null;

  const available = deriveAvailableContexts(user);
  const ctx = available.find(
    item =>
      item.membershipId === user.activeMembershipId &&
      item.organizationId === user.activeOrganizationId &&
      item.branchId === user.activeBranchId
  );
  if (!ctx && !user.delegated) return null;

  return {
    membershipId: ctx?.membershipId ?? user.activeMembershipId,
    organizationId: ctx?.organizationId ?? user.activeOrganizationId,
    organizationName: ctx?.organizationName ?? user.activeOrganizationName ?? '',
    branchId: ctx?.branchId ?? user.activeBranchId,
    branchName: ctx?.branchName ?? user.activeBranchName,
    clientAccountId: user.activeClientAccountId,
    portfolioAccountId: user.activePortfolioAccountId,
    delegated: user.delegated === true,
  };
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [auth, setAuth] = useAtom(authAtom);
  const restoredRef = useRef(false);
  const restorationGenerationRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const proactiveRefreshRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const [restorationState, setRestorationState] = useState<SessionRestorationState>('idle');
  const [restorationReason, setRestorationReason] = useState<RestorationReason | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [currentContext, setCurrentContext] = useState<CurrentContext | null>(null);
  const [contextSwitchState, setContextSwitchState] = useState<ContextSwitchState>('idle');
  const [contextSwitchError, setContextSwitchError] = useState<string | null>(null);

  const clearAllProtectedCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: contextQueryKeyPrefix, exact: false });
    queryClient.removeQueries({ queryKey: ['navigation'] });
    queryClient.removeQueries({ queryKey: ['experience'], exact: false });
    queryClient.removeQueries({ queryKey: authQueryKeys.me });
  }, [queryClient]);

  const finalizeAnonymous = useCallback(() => {
    setAccessToken(null);
    clearAllProtectedCache();
    setRestorationState('anonymous');
    setRestorationReason(null);
    setAuth(state => ({ ...state, loading: false, user: null, isAuthenticated: false }));
    setCurrentContext(null);
  }, [clearAllProtectedCache, setAuth]);

  const finalizeDegraded = useCallback(
    (reason: RestorationReason, delayMs?: number) => {
      setRestorationState('degraded');
      setRestorationReason(reason);
      setAuth(state => ({ ...state, loading: false }));
      if (delayMs) {
        if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          restoredRef.current = false;
        }, delayMs);
      }
    },
    [setAuth]
  );

  const finalizeAuthenticated = useCallback(
    (user: User) => {
      const ctx = deriveCurrentContext(user);
      const isPlatformScope = user.scopeKind === 'SYSTEM' || user.scopeKind === 'PLATFORM';
      if (!ctx && user.memberships.length > 0 && !isPlatformScope) {
        finalizeDegraded({ code: 'AUTH_PROFILE_MISSING_ACTIVE_CONTEXT' });
        return;
      }
      setCurrentContext(ctx);
      setRestorationState('authenticated');
      setRestorationReason(null);
      setAuth(state => ({
        ...state,
        user,
        isAuthenticated: true,
        loading: false,
        mfaRequired: false,
        mfaEnrolled: user.mfa.enabled,
        currentContext: ctx,
        availableContexts: deriveAvailableContexts(user),
      }));
    },
    [finalizeDegraded, setAuth]
  );

  const attemptRestoration = useCallback(async () => {
    const generation = ++restorationGenerationRef.current;
    const isCurrent = () => restorationGenerationRef.current === generation;
    setRestorationState('restoring');
    setRestorationReason(null);

    if (getAccessToken()) {
      try {
        const profile = await fetchProfile();
        if (!isCurrent()) return;
        finalizeAuthenticated(validateProfile(profile));
        return;
      } catch (error) {
        if (!isCurrent()) return;
        if (error instanceof ApiError && error.status === 401) setAccessToken(null);
        else if (error instanceof Error && error.message.startsWith('AUTH_PROFILE_')) {
          finalizeDegraded({ code: error.message });
          return;
        } else {
          finalizeDegraded({ code: 'NETWORK_ERROR' });
          return;
        }
      }
    }

    try {
      const refreshedAccessToken = await refreshAccessToken();
      if (!isCurrent()) return;
      if (!refreshedAccessToken) {
        finalizeAnonymous();
        return;
      }
      setAccessToken(refreshedAccessToken);
      const profile = await fetchProfile();
      if (!isCurrent()) return;
      finalizeAuthenticated(validateProfile(profile));
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof ApiError) {
        if (error.code === 'AUTH_TOKEN_REUSE') {
          setRestorationState('compromised');
          setRestorationReason({ code: 'AUTH_TOKEN_REUSE' });
          setAccessToken(null);
          clearAllProtectedCache();
          setAuth(state => ({ ...state, loading: false, user: null, isAuthenticated: false }));
          setCurrentContext(null);
          return;
        }
        if (['AUTH_UNAUTHORIZED', 'AUTH_FORBIDDEN', 'AUTH_SESSION_EXPIRED'].includes(error.code)) {
          finalizeAnonymous();
          return;
        }
        if (error.code === 'RATE_LIMIT_EXCEEDED') {
          finalizeDegraded({ code: 'RATE_LIMIT_EXCEEDED' }, 60_000);
          return;
        }
      }
      if (error instanceof Error && error.message.startsWith('AUTH_PROFILE_')) {
        finalizeDegraded({ code: error.message });
      } else {
        finalizeDegraded({ code: 'NETWORK_ERROR' });
      }
    }
  }, [clearAllProtectedCache, finalizeAnonymous, finalizeAuthenticated, finalizeDegraded, setAuth]);

  const invalidateRestoration = useCallback(() => {
    restorationGenerationRef.current += 1;
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user && restorationState !== 'authenticated') {
      const ctx = deriveCurrentContext(auth.user);
      const isPlatformScope = auth.user.scopeKind === 'SYSTEM' || auth.user.scopeKind === 'PLATFORM';
      if (ctx || isPlatformScope) {
        setCurrentContext(ctx);
        setRestorationState('authenticated');
      }
    }
  }, [auth.isAuthenticated, auth.user, restorationState]);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.id) return;
    let cancelled = false;
    void authControllerReadPreferences()
      .then(payload => {
        if (!cancelled) return applyServerPreferencesRuntime(payload);
        return undefined;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, auth.user?.id]);

  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    void attemptRestoration();
  }, [attemptRestoration]);

  const retry = useCallback(() => {
    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setRetryCount(count => count + 1);
    restoredRef.current = false;
    void attemptRestoration();
  }, [attemptRestoration]);

  const logoutLocal = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    setAccessToken(null);
    clearAllProtectedCache();
    setAuth(state => ({ ...state, user: null, isAuthenticated: false, loading: false }));
    setRestorationState('anonymous');
    setRestorationReason(null);
    setCurrentContext(null);
  }, [clearAllProtectedCache, setAuth]);

  const logout = useCallback(async () => {
    logoutLocal();
  }, [logoutLocal]);

  /**
   * Proactive token refresh — decodes the JWT expiry and schedules a refresh
   * before the access token expires, eliminating 401-then-refresh round-trips.
   * The refresh chain is self-sustaining: each successful refresh reschedules
   * the next one. On failure, the session is expired via logoutLocal.
   */
  const scheduleProactiveRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const token = getAccessToken();
    if (!token) return;

    const payload = parseJwtPayload(token);
    const expMs =
      payload && typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    const now = Date.now();
    const delay =
      expMs !== null ? expMs - now - PROACTIVE_REFRESH_LEAD_MS : FALLBACK_REFRESH_INTERVAL_MS;

    if (delay <= 0) {
      void proactiveRefreshRef.current();
      return;
    }

    refreshTimerRef.current = setTimeout(() => {
      void proactiveRefreshRef.current();
    }, delay);
  }, []);

  const proactiveRefresh = useCallback(async () => {
    try {
      const newToken = await refreshAccessToken();
      if (!newToken) {
        logoutLocal();
        return;
      }
      setAccessToken(newToken);
      scheduleProactiveRefresh();
    } catch {
      logoutLocal();
    }
  }, [logoutLocal, scheduleProactiveRefresh]);

  useEffect(() => {
    proactiveRefreshRef.current = proactiveRefresh;
  }, [proactiveRefresh]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }
    scheduleProactiveRefresh();
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [auth.isAuthenticated, scheduleProactiveRefresh]);

  const refreshProfile = useCallback(async () => {
    try {
      const user = validateProfile(await fetchProfile());
      const ctx = deriveCurrentContext(user);
      setCurrentContext(ctx);
      setAuth(state => ({
        ...state,
        user,
        isAuthenticated: true,
        mfaEnrolled: user.mfa.enabled,
        currentContext: ctx,
        availableContexts: deriveAvailableContexts(user),
      }));
      scheduleProactiveRefresh();
      return user;
    } catch {
      return null;
    }
  }, [scheduleProactiveRefresh, setAuth]);

  useEffect(() => {
    const handleSessionExpired = () => logoutLocal();
    const handleCrossTabAuthEvent = (event: StorageEvent) => {
      if (event.key === AUTH_CROSS_TAB_EVENT_KEY && event.newValue) logoutLocal();
    };
    const handleAuthorizationContextChanged = () => {
      clearAllProtectedCache();
      void refreshProfile();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const token = getAccessToken();
      if (!token) return;
      const payload = parseJwtPayload(token);
      const expMs =
        payload && typeof payload.exp === 'number' ? payload.exp * 1000 : null;
      if (expMs !== null && expMs - Date.now() <= PROACTIVE_REFRESH_LEAD_MS) {
        void proactiveRefreshRef.current();
      }
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener('storage', handleCrossTabAuthEvent);
    window.addEventListener(AUTHORIZATION_CONTEXT_CHANGED_EVENT, handleAuthorizationContextChanged);
    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener('storage', handleCrossTabAuthEvent);
      window.removeEventListener(AUTHORIZATION_CONTEXT_CHANGED_EVENT, handleAuthorizationContextChanged);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearAllProtectedCache, logoutLocal, refreshProfile]);

  const switchContext = useCallback(
    async (membershipId: string, branchId: string) => {
      const previousToken = getAccessToken();
      setContextSwitchState('pending');
      setContextSwitchError(null);
      try {
        const result = await authControllerSwitchContext({ membershipId, branchId: branchId });
        if (!result?.accessToken) throw new Error('AUTH_CONTEXT_SWITCH_INVALID_RESPONSE');
        await queryClient.cancelQueries({ queryKey: contextQueryKeyPrefix, exact: false });
        await queryClient.cancelQueries({ queryKey: ['experience'], exact: false });
        setAccessToken(result.accessToken);
        clearAllProtectedCache();
        await refreshProfile();
        scheduleProactiveRefresh();
        setContextSwitchState('success');
      } catch (error) {
        if (previousToken) setAccessToken(previousToken);
        setContextSwitchState('error');
        setContextSwitchError(error instanceof Error ? error.message : 'AUTH_CONTEXT_SWITCH_FAILED');
        throw error;
      }
    },
    [clearAllProtectedCache, queryClient, refreshProfile, scheduleProactiveRefresh]
  );

  const exitDelegation = useCallback(async () => {
    const result = await authControllerExitDelegation();
    if (!result?.accessToken) throw new Error('AUTH_DELEGATION_EXIT_INVALID_RESPONSE');
    await queryClient.cancelQueries({ queryKey: contextQueryKeyPrefix, exact: false });
    await queryClient.cancelQueries({ queryKey: ['experience'], exact: false });
    setAccessToken(result.accessToken);
    clearAllProtectedCache();
    await refreshProfile();
    scheduleProactiveRefresh();
  }, [clearAllProtectedCache, queryClient, refreshProfile, scheduleProactiveRefresh]);

  const profileQueryKey = useMemo(
    () => buildProfileQueryKey(currentContext, auth.user),
    [currentContext, auth.user]
  );
  const profileQuery = useQuery({
    queryKey: profileQueryKey,
    queryFn: async () => validateProfile(await authControllerMe()),
    enabled: restorationState === 'authenticated' && !auth.mfaRequired && getAccessToken() !== null,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const user = profileQuery.data ?? auth.user;
  const isAuthenticated = !!user && (restorationState === 'authenticated' || auth.isAuthenticated);
  const loading = auth.loading || (restorationState === 'restoring' && profileQuery.isLoading);
  const availableContexts = deriveAvailableContexts(user);

  return (
    <SessionContext.Provider
      value={{
        retry,
        retryCount,
        user,
        isAuthenticated,
        loading,
        restorationState,
        restorationReason,
        logout,
        refreshProfile,
        currentContext,
        availableContexts,
        switchContext,
        exitDelegation,
        contextSwitchState,
        contextSwitchError,
        invalidateRestoration,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  return useContext(SessionContext);
}
